import type { RxJsonSchema } from 'rxdb'

/**
 * Member Document Type (Denormalized for RxDB)
 */
export interface MemberDocument {
  id: string
  envId: string
  userId: string
  isDefault: boolean
  email: string | null
  name: string | null
  profilePictureUrl: string | null
  role: string | null
  isSuperAgent: boolean
  lastActiveAt?: number | null
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Member
 */
export const memberSchema: RxJsonSchema<MemberDocument> = {
  version: 2,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    userId: {
      type: 'string',
      maxLength: 36,
    },
    isDefault: {
      type: 'boolean',
      default: false,
    },
    email: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    name: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    profilePictureUrl: {
      type: ['string', 'null'],
      maxLength: 500,
    },
    role: {
      type: ['string', 'null'],
      maxLength: 50,
    },
    isSuperAgent: {
      type: 'boolean',
      default: false,
    },
    lastActiveAt: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'userId',
    'isDefault',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: ['envId', 'userId', 'updatedOn', ['envId', 'isDeleted']],
}

export const memberMigrationStrategies = {
  // v0 -> v1: replaced `firstName`/`lastName`/`displayName` with a single
  // `name` field; added `lastActiveAt`. Synthesize `name` from the old parts
  // when present, otherwise leave null.
  1: (oldDoc: any): any => {
    const { firstName, lastName, displayName, ...rest } = oldDoc
    const synthesized = displayName || [firstName, lastName].filter(Boolean).join(' ').trim() || null
    return {
      ...rest,
      name: oldDoc.name ?? synthesized,
      lastActiveAt: oldDoc.lastActiveAt ?? null,
    }
  },
  // v1 -> v2: added `isSuperAgent` (server-authoritative; defaults false).
  2: (oldDoc: any): any => ({
    ...oldDoc,
    isSuperAgent: oldDoc.isSuperAgent ?? false,
  }),
}
